/**
 * Google Workspace MCP — Unified service for calling Google APIs via MCP Gateway.
 *
 * This replaces direct Google API calls (gmail.ts, googleCalendar.ts, googleDrive.ts)
 * with calls through the MCP API Gateway, which handles OAuth server-side.
 *
 * Benefits:
 * - Single OAuth flow (user connects once, works everywhere)
 * - Tokens never touch the browser (stored in AWS Secrets Manager)
 * - Same API for agent tools AND panel UI
 * - Supports all Google Workspace services
 *
 * Auth flow:
 * 1. User clicks "Connect" → opens popup to MCP OAuth URL
 * 2. MCP handles Google OAuth, stores tokens server-side
 * 3. Frontend stores tenant_id (email) in localStorage
 * 4. All API calls include tenant_id → MCP resolves tokens server-side
 */

const MCP_BASE_URL = 'https://4xhkyip9jh.execute-api.us-east-1.amazonaws.com'
const TENANT_ID_KEY = 'vsm_google_mcp_tenant_id'
const MCP_USER_KEY = 'vsm_google_mcp_user'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface McpGoogleUser {
  email: string
  displayName?: string
  photoURL?: string
}
export interface McpCallResult<T = any> {
  success: boolean
  data?: T
  error?: string
}

// ─── Tenant Management ────────────────────────────────────────────────────────

/** Get the stored tenant ID (user's Google email) */
export function getMcpTenantId(): string | null {
  try {
    return localStorage.getItem(TENANT_ID_KEY)
  } catch {
    return null
  }
}

/** Check if MCP Google is connected */
export function isMcpConnected(): boolean {
  return getMcpTenantId() !== null
}

/** Store tenant ID after OAuth */
export function setMcpTenantId(email: string): void {
  try {
    localStorage.setItem(TENANT_ID_KEY, email)
  } catch {}
}

/** Get stored MCP Google user info */
export function getMcpGoogleUser(): McpGoogleUser | null {
  try {
    const raw = localStorage.getItem(MCP_USER_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

/** Store MCP Google user info */
export function setMcpGoogleUser(user: McpGoogleUser): void {
  try {
    localStorage.setItem(MCP_USER_KEY, JSON.stringify(user))
  } catch {}
}

/** Clear MCP Google connection */
export function clearMcpConnection(): void {
  try {
    localStorage.removeItem(TENANT_ID_KEY)
    localStorage.removeItem(MCP_USER_KEY)
  } catch {}
}

// ─── OAuth Flow ───────────────────────────────────────────────────────────────

/**
 * Start the MCP Google OAuth flow.
 * Opens a popup window that redirects to Google OAuth via the MCP Gateway.
 * Returns the tenant_id (email) on success.
 *
 * @param email - Required. The user's Google email address.
 */
export async function mcpGoogleSignIn(email: string): Promise<McpGoogleUser> {
  // Validate email is provided
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    throw new Error('Se necesita un email válido de Google para conectar.')
  }

  // Step 1: Get the OAuth URL from the MCP Gateway
  const oauthUrlRes = await fetch(
    `${MCP_BASE_URL}/oauth/url?tenant_id=${encodeURIComponent(email)}`,
  )
  if (!oauthUrlRes.ok) {
    throw new Error('No se pudo obtener la URL de autenticación de Google.')
  }
  const oauthData = await oauthUrlRes.json()
  const oauthUrl = oauthData.oauth_url || oauthData.url
  console.log(
    '[MCP OAuth] Got URL:',
    oauthUrl ? oauthUrl.substring(0, 80) + '...' : 'MISSING',
  )
  if (!oauthUrl) {
    throw new Error(
      'El servidor MCP no devolvió una URL de autenticación válida.',
    )
  }

  // Step 2: Open popup for OAuth
  return new Promise((resolve, reject) => {
    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const popup = window.open(
      oauthUrl,
      'google-mcp-oauth',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`,
    )
    if (!popup) {
      reject(
        new Error(
          'No se pudo abrir la ventana de autenticación. Habilitá popups para este sitio.',
        ),
      )
      return
    }

    // Poll for popup close + check if auth succeeded
    const pollInterval = setInterval(async () => {
      try {
        if (popup.closed) {
          clearInterval(pollInterval)

          // Check if OAuth succeeded by calling a simple MCP endpoint
          try {
            const checkRes = await fetch(
              `${MCP_BASE_URL}/oauth/status?tenant_id=${encodeURIComponent(email)}`,
            )
            if (checkRes.ok) {
              const status = await checkRes.json()
              if (status.connected) {
                const user: McpGoogleUser = {
                  email,
                  displayName: status.displayName || email.split('@')[0],
                  photoURL: status.photoURL || undefined,
                }
                setMcpTenantId(email)
                setMcpGoogleUser(user)
                resolve(user)
                return
              }
            }
          } catch {}

          // Fallback: assume success if popup closed (user may have completed OAuth)
          // Verify by making a test call
          try {
            const testRes = await mcpCall('calendar_list_calendars', {}, email)
            if (testRes.success) {
              const user: McpGoogleUser = {
                email,
                displayName: email.split('@')[0],
              }
              setMcpTenantId(email)
              setMcpGoogleUser(user)
              resolve(user)
              return
            }
          } catch {}
          reject(
            new Error('La autenticación con Google fue cancelada o falló.'),
          )
        }
      } catch {
        // Cross-origin — popup still open, keep polling
      }
    }, 1000)

    // Timeout after 5 minutes
    setTimeout(
      () => {
        clearInterval(pollInterval)
        try {
          popup.close()
        } catch {}
        reject(
          new Error('La autenticación con Google expiró. Intentá de nuevo.'),
        )
      },
      5 * 60 * 1000,
    )
  })
}

/** Sign out of MCP Google */
export function mcpGoogleSignOut(): void {
  clearMcpConnection()
}

// ─── Generic MCP Tool Caller ──────────────────────────────────────────────────

/**
 * Call a Google Workspace MCP tool.
 * This is the core function used by gmail.ts, googleCalendar.ts, googleDrive.ts.
 *
 * @param toolName - The MCP tool name (e.g., 'gmail_list_messages', 'calendar_list_events')
 * @param params - Tool parameters (without tenant_id — added automatically)
 * @param overrideTenantId - Optional tenant_id override (for testing)
 */
export async function mcpCall<T = any>(
  toolName: string,
  params: Record<string, any> = {},
  overrideTenantId?: string,
): Promise<McpCallResult<T>> {
  const tenantId = overrideTenantId || getMcpTenantId()
  if (!tenantId) {
    return {
      success: false,
      error:
        'Google no está conectado. Conectá tu cuenta desde el panel de Conectores.',
    }
  }
  try {
    const response = await fetch(`${MCP_BASE_URL}/tool/${toolName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        params: {
          tenant_id: tenantId,
          ...params,
        },
      }),
    })
    if (!response.ok) {
      // Handle auth errors
      if (response.status === 401 || response.status === 403) {
        return {
          success: false,
          error:
            'Token de Google expirado. Reconectá tu cuenta desde Conectores.',
        }
      }
      const errorText = await response.text().catch(() => response.statusText)
      return {
        success: false,
        error: `Google MCP error ${response.status}: ${errorText}`,
      }
    }
    const data = await response.json()
    return {
      success: true,
      data,
    }
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Error de conexión con Google MCP',
    }
  }
}

/**
 * Call MCP and throw on error (for use in service functions that expect exceptions).
 * This matches the existing pattern in gmail.ts, googleCalendar.ts, googleDrive.ts.
 */
export async function mcpCallOrThrow<T = any>(
  toolName: string,
  params: Record<string, any> = {},
): Promise<T> {
  const startTime = performance.now()
  const result = await mcpCall<T>(toolName, params)
  const durationMs = Math.round(performance.now() - startTime)

  // Determine service from tool name prefix
  const service = toolName.split('_')[0] || 'google' // gmail, calendar, drive, docs, sheets, slides, forms

  if (!result.success) {
    // Log failed calls
    try {
      const { pushDebugLog } = await import('../hooks/useDebugLogs')
      pushDebugLog({
        source: 'google',
        tool: toolName,
        params,
        rawResponse: result,
        processedData: null,
        success: false,
        error: result.error || 'Error en Google MCP',
        durationMs,
      })
    } catch {}
    throw new Error(result.error || 'Error en Google MCP')
  }

  // Unwrap MCP gateway's { result: ... } wrapper if present
  const data = result.data as any
  let unwrapped: T
  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    'result' in data
  ) {
    unwrapped = data.result as T
  } else {
    unwrapped = data as T
  }

  // Log successful calls
  try {
    const { pushDebugLog } = await import('../hooks/useDebugLogs')
    pushDebugLog({
      source: 'google',
      tool: toolName,
      params,
      rawResponse: result.data,
      processedData: unwrapped,
      success: true,
      durationMs,
    })
  } catch {}
  return unwrapped
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export { MCP_BASE_URL }
